import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import PageHeader from '../../../components/ui/PageHeader';
import { EXAM_INFO, SKILLS } from '../../../data/exam/b2';
import { examB2API } from '../../../services/api';
import {
  BookOpenIcon,
  SpeakerWaveIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const SKILL_ICONS = {
  lesen: BookOpenIcon,
  hoeren: SpeakerWaveIcon,
  schreiben: PencilSquareIcon,
  sprechen: ChatBubbleLeftRightIcon,
};

const GoetheB2HubPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(examB2API.getVariant?.() || 'erwachsene');

  useEffect(() => {
    examB2API.getProgress(variant)
      .then((res) => setProgress(res.data.data))
      .catch(() => setProgress(null))
      .finally(() => setLoading(false));
  }, [variant]);

  return (
    <DashboardLayout>
      <div className="page-shell-wide">
        <PageHeader
          icon={AcademicCapIcon}
          badge="Goethe-Zertifikat B2"
          title={EXAM_INFO.name}
          description={EXAM_INFO.description}
        >
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="button"
              size="sm"
              variant={variant === 'erwachsene' ? 'default' : 'outline'}
              onClick={() => { examB2API.setVariant('erwachsene'); setVariant('erwachsene'); }}
            >
              Exam 1 — Erwachsene
            </Button>
            <Button
              type="button"
              size="sm"
              variant={variant === 'jugendliche' ? 'default' : 'outline'}
              onClick={() => { examB2API.setVariant('jugendliche'); setVariant('jugendliche'); }}
            >
              Exam 2 — Jugendliche
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="outline">Kalon me ≥ {EXAM_INFO.passScore}%</Badge>
            <Badge variant="outline">4 module</Badge>
            {progress?.all_modules_passed && (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircleIcon className="w-3 h-3" /> Të gjitha kaluar
              </Badge>
            )}
          </div>
        </PageHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SKILLS.map((skill) => {
            const Icon = SKILL_ICONS[skill.id];
            const skillProgress = progress?.skills?.[skill.id];
            return (
              <Card
                key={skill.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(skill.path)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(skill.path); }}
                className="p-5 text-left cursor-pointer hover:border-zinc-400 transition-colors group"
              >
                <div className="flex items-start justify-between mb-4">
                  <Icon className="w-6 h-6 text-brand-text" />
                  <div className="text-right">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-muted border border-brand-border px-2 py-0.5">
                      {skill.duration}
                    </span>
                    {!loading && skillProgress?.best_score > 0 && (
                      <p className={`text-xs font-bold mt-1 ${skillProgress.passed ? 'text-brand-cta' : 'text-brand-muted'}`}>
                        Best: {skillProgress.best_score}%
                      </p>
                    )}
                  </div>
                </div>
                <h2 className="text-base font-bold text-brand-text mb-1">
                  {skill.title}
                  <span className="text-brand-muted font-normal text-sm ml-1">({skill.subtitle})</span>
                </h2>
                <p className="text-sm text-brand-muted mb-3 leading-relaxed">{skill.description}</p>
                <span className="flex items-center gap-1 text-xs font-bold text-brand-text group-hover:underline">
                  Fillo <ArrowRightIcon className="w-3.5 h-3.5" />
                </span>
              </Card>
            );
          })}
        </div>

        <Card className="mt-6">
          <CardContent className="p-5 text-sm text-brand-muted leading-relaxed">
            <p className="font-bold text-brand-text text-xs uppercase tracking-wide mb-2">Këshilla</p>
            <ul className="space-y-1 text-xs">
              <li>• Rezultatet ruhen në llogarinë tuaj.</li>
              <li>• Secili modul vlerësohet veç — duhet ≥ 60% në secilin.</li>
              <li>• Modulet Lesen/Hören përdorin formatin zyrtar të provimit.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default GoetheB2HubPage;
